<!-- src/components/dashboard/SanityContent.vue -->
<template>
    <div v-if="!loading" class="h-screen">
        <div v-if="$route.meta.module === 'matrix' || $route.meta.module === 'laying-birds'"
            class="flex flex-col gap-3">
            <div class="flex flex-col gap-2">
                <!-- Temperature and Humidity -->
                <div class="flex flex-wrap gap-2">
                    <div v-for="(division, index) in getSelectedBatch.batchDivisions.filter(division => division.s0.length > 0)"
                        :key="index"
                        class="bg-white rounded border border-gray-150 shadow-sm flex flex-col items-center p-3">
                        <!-- título -->
                        <strong class="text-lg">
                            {{ $t(division.type) }} {{ division.identification }}
                        </strong>

                        <!-- gráficos -->
                        <div class="flex flex-row gap-3 justify-center">
                            <ChartComponent :id="'divisionGraph_temp_01_' + division.id" class="w-[12rem] h-[11rem]"
                                type="gauge" :graph-props="convertGraphDataGauge(
                                    division.s0[0].environmentTemperature,
                                    'environmentTemperature',
                                    getSelectedBatch.environmentTemperature,
                                    $t('ambient') ?? '',
                                    '°C'
                                )?.props || {}
                                    " />
                            <ChartComponent :id="'divisionGraph_hum_02_' + division.id" class="w-[12rem] h-[11rem]"
                                type="gauge" :graph-props="convertGraphDataGauge(
                                    division.s0[0].humidity,
                                    'humidity',
                                    getSelectedBatch.humidity,
                                    $t('humidity'),
                                    '%'
                                )?.props || {}
                                    " />
                        </div>

                        <!-- timestamp -->
                        <strong>
                            {{
                                division.s0[0]?.timestamp
                                    ? new Date(division.s0[0]?.timestamp).toLocaleString()
                                    : ""
                            }}
                        </strong>
                    </div>
                </div>

                <!-- CO2 -->
                <div class="bg-white rounded border border-gray-150 text-red-400 font-bold text-center p-2">
                    CO²
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div class="bg-white rounded border p-2 border-gray-150">
                        <ChartComponent id="divisionGraph_co2_01" :graph-props="convertGraphDataLine(
                            mapSerieData(getSelectedBatch.batchDivisions, 'd0', 'co2'),
                            getSelectedBatch.co2,
                            $t('last-days'),
                            'ppm'
                        ).props || {}" />
                    </div>

                    <div class="bg-white rounded border p-2 border-gray-150">
                        <ChartComponent id="divisionGraph_co2_02" :graph-props="convertGraphDataLine(
                            mapSerieData(getSelectedBatch.batchDivisions, 'd0', 'co2'),
                            getSelectedBatch.co2,
                            $t('last 24h'),
                            'ppm', true
                        ).props || {}" />
                    </div>
                </div>

                <!-- Ambient Temperature -->
                <div class="bg-white rounded border border-gray-150 text-red-400 font-bold text-center p-2">
                    {{ $t('ambient-temperature') }}
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div class="bg-white rounded border p-2 border-gray-150">
                        <ChartComponent id="divisionGraph_envTemp_01" :graph-props="convertGraphDataLine(
                            mapSerieData(getSelectedBatch.batchDivisions, 's0', 'environmentTemperature'),
                            getSelectedBatch.environmentTemperature,
                            $t('last-days'),
                            'ºC'
                        ).props || {}" />
                    </div>

                    <div class="bg-white rounded border p-2 border-gray-150">
                        <ChartComponent id="divisionGraph_envTemp_02" :graph-props="convertGraphDataLine(
                            mapSerieData(getSelectedBatch.batchDivisions, 's0', 'environmentTemperature'),
                            getSelectedBatch.environmentTemperature,
                            $t('last 24h'),
                            'ºC', true
                        ).props || {}" />
                    </div>
                </div>

                <!-- Ambient Humidity -->
                <div class="bg-white rounded border border-gray-150 text-red-400 font-bold text-center p-2">
                    {{ $t('ambient-humidity') }}
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div class="bg-white rounded border p-2 border-gray-150">
                        <ChartComponent id="divisionGraph_humidity_01" :graph-props="convertGraphDataLine(
                            mapSerieData(getSelectedBatch.batchDivisions, 's0', 'humidity'),
                            getSelectedBatch.humidity,
                            $t('last-days'),
                            '%'
                        ).props || {}" />
                    </div>

                    <div class="bg-white rounded border p-2 border-gray-150">
                        <ChartComponent id="divisionGraph_humidity_02" :graph-props="convertGraphDataLine(
                            mapSerieData(getSelectedBatch.batchDivisions, 's0', 'humidity'),
                            getSelectedBatch.humidity,
                            $t('last 24h'),
                            '%', true
                        ).props || {}" />
                    </div>
                </div>
            </div>
            <div class="flex flex-col gap-2">
                <div class="bg-white rounded border border-gray-150 text-red-400 font-bold text-center p-2">
                    {{ $t('ambient-temperature') }}
                </div>
                <div v-for="(division, index) in getSelectedBatch.batchDivisions.filter(division => division.s0.length > 0)"
                    :key="index" class="flex flex-col gap-1">
                    <!-- título -->
                    <div class="bg-white rounded border border-gray-150 font-bold text-center p-2">
                        {{ $t(division.type) }} {{ division.identification }}
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div class="bg-white rounded border p-2 border-gray-150">
                            <ChartComponent :id="'divisionGraph_amp_01_' + division.id" :graph-props="convertGraphDataLine(
                                mapGroupedSerieDataByDate(division, 's0', 'environmentTemperature'),
                                {},
                                $t('last-days'),
                                'ºC', false, true
                            ).props || {}" />
                        </div>
                        <div class="bg-white rounded border p-2 border-gray-150">
                            <ChartComponent :id="'divisionGraph_amp_02_' + division.id" :graph-props="convertGraphBar(
                                mapGroupedSerieDataByAge(division, 's0', 'environmentTemperature', true),
                                $t('amplitude'),
                                'ºC'
                            ).props || {}" />
                        </div>
                    </div>
                </div>
            </div>
            <div class="flex flex-col gap-2">
                <div class="bg-white rounded border border-gray-150 text-red-400 font-bold text-center p-2">
                    {{ $t('comfort_map') }}
                </div>
                <div v-for="(division, index) in getSelectedBatch.batchDivisions.filter(division => division.s0.length > 0)"
                    :key="index" class="flex flex-col gap-1">
                    <!-- título -->
                    <div class="bg-white rounded border border-gray-150 font-bold text-center p-2">
                        {{ $t(division.type) }} {{ division.identification }}
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div class="bg-white rounded border p-2 border-gray-150 md:col-span-2">
                            <ChartComponent :id="'divisionGraph_comfort_01_' + division.id"
                                :graph-props="convertGraphColumn(division.s0).props || {}" />
                        </div>
                        <div class="bg-white rounded border p-2 border-gray-150 md:col-span-1">
                            <ChartComponent :id="'divisionGraph_comfort_02_' + division.id"
                                :graph-props="convertGraphDonut(division.s0).props || {}" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <iframe v-else class="rounded h-full w-full" :src="grafanaDashboardUrl" frameborder="0">
        </iframe>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { differenceInSeconds } from 'date-fns';
import { parseISO } from 'date-fns';
import ChartComponent from "@/components/dashboard/ChartHighcharts";

export default {
    name: 'SanityContent',
    components: {
        ChartComponent
    },
    data() {
        return {
            enthalpyColorMap: {
                'comfortable': '#4CAF50',
                'intermediary': '#FFC107',
                'critical': '#FF9800',
                'lethal': '#F44336'
            }
        };
    },
    props: {
        loading: Boolean,
    },
    computed: {
        ...mapGetters('environment', [
            'getSelectedBatch',
            'getDashboardsSettings',
        ]),
        grafanaDashboardUrl() {
            const batch = this.getSelectedBatch
            if (!batch) return ''

            const dashboardUid =
                this.getDashboardsSettings?.sanity[this.$i18n.locale]

            const baseUrl = process.env.VUE_APP_GRAFANA_URL

            const url = new URL(
                `d/${dashboardUid}`,
                baseUrl
            )

            url.searchParams.append('theme', 'light');
            url.searchParams.append('kiosk', '1')
            url.searchParams.append('embed', 'true')

            let openingDate = differenceInSeconds(new Date(), parseISO(batch.openingDate)) + 60;
            let closingDate = differenceInSeconds(new Date(), parseISO(batch.closingDate)) + 60;

            url.searchParams.append('var-batch_id', batch.id);
            url.searchParams.append('from', openingDate > 0 ? `now-${openingDate}s` : 'now-24h');
            url.searchParams.append('to', closingDate > 0 ? `now-${closingDate}s` : `now`);

            return url.toString()
        }

    },
    methods: {
        ...mapActions('environment', [
            'loadBatchDetails',
        ]),
        getYAxixGauge(type, min, max, idealMin, idealMax) {
            switch (type) {
                case 'environmentTemperature':
                    return {
                        stops: [
                            [0, '#DF5353'],
                            [min / 40, '#DF5353'],
                            [min / 40, '#55BF3B'],
                            [((max - min) * .8 + min) / 40, '#55BF3B'],
                            [((max - min) * .8 + min) / 40, '#EAB839'],
                            [max / 40, '#EAB839'],
                            [max / 40, '#DF5353'],
                            [1, '#DF5353']
                        ],
                        min: 0,
                        max: 40,
                        tickAmount: 2,
                        lineWidth: 0,
                        tickWidth: 0,
                        minorTickInterval: null,
                        labels: {
                            enabled: false,
                            distance: 10,
                            style: {
                                fontSize: '14px'
                            }
                        },
                        plotBands: [{
                            from: 0,
                            to: min,
                            color: '#DF5353', // red
                            thickness: 8,
                            borderRadius: '50%'
                        }, {
                            from: min,
                            to: idealMin,
                            color: '#EAB839', // yellow
                            thickness: 8,
                            borderRadius: '50%'
                        }, {
                            from: idealMin,
                            to: idealMax,
                            color: '#55BF3B', // green
                            thickness: 8,
                            borderRadius: '50%'
                        },
                        {
                            from: idealMax,
                            to: max,
                            color: '#EAB839', // yellow
                            thickness: 8,
                            borderRadius: '50%'
                        }, {
                            from: max,
                            to: 999999,
                            color: '#DF5353', // red
                            thickness: 8,
                            borderRadius: '50%'
                        }]
                    }
                case 'humidity':
                    return {
                        stops: [
                            [0, '#DF5353'],
                            [min / 100, '#DF5353'],
                            [min / 100, '#EAB839'],
                            [(min + (max - min) * .1) / 100, '#EAB839'],
                            [(min + (max - min) * .1) / 100, '#55BF3B'],
                            [(min + (max - min) * .9) / 100, '#55BF3B'],
                            [(min + (max - min) * .9) / 100, '#EAB839'],
                            [max / 100, '#EAB839'],
                            [max / 100, '#DF5353'],
                            [1, '#DF5353']
                        ],
                        min: 0,
                        max: 100,
                        tickAmount: 2,
                        lineWidth: 0,
                        tickWidth: 0,
                        minorTickInterval: null,
                        labels: {
                            enabled: false,
                            distance: 10,
                            style: {
                                fontSize: '14px'
                            }
                        },
                        plotBands: [{
                            from: 0,
                            to: min,
                            color: '#DF5353', // green
                            thickness: 8,
                            borderRadius: '50%'
                        }, {
                            from: min,
                            to: min + (max - min) * .1,
                            color: '#EAB839', // yellow
                            thickness: 8,
                            borderRadius: '50%'
                        }, {
                            from: min + (max - min) * .1,
                            to: min + (max - min) * .9,
                            color: '#55BF3B', // red
                            thickness: 8,
                            borderRadius: '50%'
                        }, {
                            from: min + (max - min) * .9,
                            to: max,
                            color: '#EAB839', // yellow
                            thickness: 8,
                            borderRadius: '50%'
                        }, {
                            from: max,
                            to: 100,
                            color: '#DF5353', // yellow
                            thickness: 8,
                            borderRadius: '50%'
                        },
                        ]
                    }

                default:
                    return null;
            }
        },
        mapSerieData(data, key, subKey) {
            return data.map(item => ({
                name: item.type + ' ' + item.identification,
                data: item[key].map(d => ({ x: d.timestamp, y: d[subKey], age: d.age })),
            })).filter(item => item.data.length > 0);
        },
        mapGroupedSerieDataByDate(data, key, subKey) {
            const groupedData = Object.values(
                data[key].reduce((acc, item) => {
                    const day = new Date(item.timestamp).setHours(0, 0, 0, 0);

                    if (!acc[day]) {
                        acc[day] = { date: day, values: [], age: item.age };
                    }
                    if (item[subKey] !== null && item[subKey] !== undefined) {
                        acc[day].values.push(item[subKey]);
                    }
                    return acc;
                }, {})
            ).map(group => {
                const { date, values } = group;
                const max = Math.max(...values);
                const min = Math.min(...values);
                const avg = values.reduce((a, b) => a + b, 0) / values.length;

                return { date, max, min, avg, age: group.age };
            });

            return [{
                name: this.$t(data.type) + ' ' + data.identification,
                data: groupedData.map(item => ({ x: item.date, y: item.avg, age: item.age })),
                zIndex: 1,
            },
            {
                name: 'Range',
                data: groupedData.map(item => ([item.date, item.max, item.min])),
                type: 'arearange',
                lineWidth: 0,
                linkedTo: ':previous',
                fillOpacity: 0.3,
                zIndex: 0
            }
            ];
        },
        mapGroupedSerieDataByAge(data, key, subKey, range = false) {
            const groupedData = Object.values(
                data[key].reduce((acc, item) => {
                    const age = item.age;

                    if (!acc[age]) {
                        acc[age] = { age, values: [] };
                    }
                    acc[age].values.push(item[subKey]);
                    return acc;
                }, {})
            ).map(group => {
                const { age, values } = group;
                const max = Math.max(...values);
                const min = Math.min(...values);
                const data = values;

                const amplitude = max - min;

                return { age, amplitude, data };
            });

            if (range) {
                return [{
                    name: this.$t(data.type) + ' ' + data.identification,
                    data: groupedData.map(item => ({ x: item.age, y: item.amplitude })),
                    zIndex: 1,
                }];
            } else {
                return groupedData;
            }
        },
        convertGraphDataGauge(data, key, rule, label, symbol) {
            let { min, max, idealMin, idealMax } = rule || {};

            if (key === "humidity") {
                min = min > 0 ? min : 51;
                max = max ? max / 10 : 71;
            } else if (key === "environmentTemperature") {
                min = min > 0 ? min : 15;
                max = max ? max : 40;
            }

            return {
                id: 1,
                props: {
                    chart: {
                        type: 'solidgauge',
                        plotBackgroundColor: null,
                        plotBackgroundImage: null,
                        plotBorderWidth: 0,
                        plotShadow: false,
                    },
                    width: 100,
                    height: 250,
                    periodFrom: null,
                    periodTo: null,
                    legend: { enabled: false },
                    title: {
                        text: label,
                        align: 'center',
                        verticalAlign: 'bottom',
                        margin: 50,
                        floating: true,
                        style: {
                            fontSize: '14px',
                            fontWeight: 'normal',
                            textTransform: 'none'
                        }
                    },
                    tooltip: {
                        enabled: false
                    },
                    pane: {
                        startAngle: -110,
                        endAngle: 110,
                        background: null,
                        center: ['50%', '65%'],
                        size: '120%'
                    },
                    plotOptions: {
                        solidgauge: {
                            animation: false,
                            innerRadius: '50%',
                            radius: '88%',
                            borderRadius: 3,
                            dataLabels: {
                                y: 5,
                                borderWidth: 0,
                                useHTML: true
                            }
                        }
                    },
                    yAxis: this.getYAxixGauge(key, min, max, idealMin, idealMax),
                    credits: {
                        enabled: false
                    },
                    series: [{
                        name: label,
                        data: [data || 0],
                        dataLabels: {
                            y: -10,
                            format: `{point.y:,.1f}${symbol}`,
                            borderWidth: 0,
                            color: '#333333',
                            style: {
                                fontSize: '20px'
                            }
                        }
                    }],
                }
            }
        },
        convertGraphDonut(data) {
            const grouped = {};
            data.forEach(item => {
                if (!grouped[item.enthalpy.status]) {
                    grouped[item.enthalpy.status] = {
                        name: this.$t(item.enthalpy.status),
                        color: this.enthalpyColorMap[item.enthalpy.status] || '#9E9E9E',
                        total: 0
                    };
                }
                grouped[item.enthalpy.status].total += 1;
            });

            const series = Object.values(grouped).map(gr => ({
                name: gr.name,
                color: gr.color,
                y: gr.total
            }));

            return {
                props: {
                    width: 100,
                    height: 100,
                    chart: {
                        type: 'pie',
                    },
                    xAxis: {
                        min: 0,
                        max: 100,
                    },
                    yAxis: {
                        min: 0,
                        max: 100,
                    },
                    accessibility: {
                        point: {
                            valueSuffix: '%'
                        }
                    },
                    title: {
                        text: this.$t('total')
                    },
                    tooltip: {
                        headerFormat: null,
                        pointFormat: '<span style="color:{point.color}">{point.name}</span>' +
                            ':  ({point.percentage:.0f}%)',
                    },
                    credits: {
                        enabled: false
                    },
                    legend: {
                        enabled: true,
                        layout: 'horizontal',
                        align: 'center',
                        verticalAlign: 'bottom',
                        itemStyle: {
                            fontSize: '12px'
                        }
                    },
                    plotOptions: {
                        series: {
                            animation: false,
                            allowPointSelect: true,
                            cursor: 'pointer',
                            borderRadius: 8,
                            dataLabels: {
                                enabled: false,
                                distance: 20,
                                format: '{point.name}'
                            },
                            showInLegend: true
                        }
                    },
                    series: [{
                        name: 'Percentual',
                        colorByPoint: true,
                        innerSize: '75%',
                        data: series
                    }]
                }
            }
        },
        convertGraphColumn(data) {
            const ages = [...new Set(data.map(d => d.age))].sort((a, b) => a - b);

            const grouped = {};
            data.forEach(item => {
                if (!grouped[item.enthalpy.status]) {
                    grouped[item.enthalpy.status] = {
                        name: this.$t(item.enthalpy.status),
                        color: this.enthalpyColorMap[item.enthalpy.status] || '#9E9E9E',
                        values: {}
                    };
                }
                grouped[item.enthalpy.status].values[item.age] =
                    (grouped[item.enthalpy.status].values[item.age] || 0) + 1;
            });

            const series = Object.values(grouped).map(gr => ({
                name: gr.name,
                color: gr.color,
                data: ages.map(age => gr.values[age] || 0)
            }));

            return {
                props: {
                    chart: {
                        type: 'column'
                    },
                    title: {
                        text: this.$t('last-days')
                    },
                    height: 250,
                    legend: { enabled: true },
                    tooltip: {
                        headerFormat: null,
                        pointFormat: '<span style="color:{series.color}">{series.name}</span>' +
                            '<b>: {point.y}</b> ({point.percentage:.0f}%)<br/>',
                        shared: true
                    },
                    plotOptions: {
                        column: {
                            animation: false,
                            stacking: 'percent',
                            dataLabels: {
                                enabled: false,
                                format: '{point.percentage:.0f}%'
                            }
                        }
                    },
                    credits: {
                        enabled: false
                    },
                    yAxis: {
                        min: 0,
                        title: {
                            text: this.$t('percent')
                        }
                    },
                    xAxis: {
                        categories: ages,
                        title: {
                            text: this.$t('age')
                        }
                    },
                    series: series,
                }
            }
        },
        convertGraphBar(data, title, unit) {
            return {
                props: {
                    chart: {
                        type: 'column'
                    },
                    height: 250,
                    legend: { enabled: false },
                    title: {
                        text: title
                    },
                    tooltip: {
                        headerFormat: '<b style="color:{point.color}">{series.name}</b><br/><br/>',
                        pointFormat: '<b>{point.y:.2f}%</b>'
                    },
                    credits: {
                        enabled: false
                    },
                    plotOptions: {
                        series: {
                            animation: false,
                            borderWidth: 0,
                            dataLabels: {
                                enabled: true,
                                format: `{point.y:.1f}${unit}`
                            }
                        }
                    },
                    yAxis: {
                        min: 0,
                        title: {
                            enabled: false,
                        },
                        labels: {
                            format: `{value} ${unit}`
                        },
                    },
                    xAxis: {
                        type: 'category'
                    },
                    series: data,
                }
            }
        },
        convertGraphDataLine(data, rule, title, unit, last24h = false, dateOnly = false) {
            const plotBands = rule?.max || rule?.min ? {
                from: rule?.max || 2000,
                to: rule?.min || 0,
                color: 'rgba(68, 170, 213, 0.2)'
            } : {}

            const minTime = Date.now() - 24 * 60 * 60 * 1000;

            const series = (data || []).map(serie => {
                const firstPoint = (serie.data && serie.data[0]) || {};
                const isRange = ('low' in firstPoint) || ('high' in firstPoint);

                if (isRange) {
                    const pts = (serie.data || []).map(pt => ({
                        x: new Date(pt.x).getTime(),
                        low: Number(pt.low ?? pt.low),
                        high: Number(pt.high ?? pt.high)
                    }));

                    return {
                        ...serie,
                        type: serie.type || 'arearange',
                        data: last24h ? pts.filter(p => p.x >= minTime) : pts
                    };
                } else {
                    const pts = (serie.data || []).map(pt => [
                        new Date(pt.x).getTime(),
                        Number(pt.y),
                        pt.age !== undefined ? pt.age : null
                    ]);

                    const filtered = last24h ? pts.filter(p => p[0] >= minTime) : pts;

                    return {
                        ...serie,
                        type: serie.type || 'line',
                        keys: ['x', 'y', 'age'],
                        data: filtered
                    };
                }
            });

            return {
                props: {
                    chart: {
                        zooming: {
                            type: 'xy'
                        }
                    },
                    height: 250,
                    legend: { enabled: true },
                    title: {
                        text: title
                    },
                    tooltipLabels: {
                        age: this.$t('age')
                    },
                    tooltip: {
                        shared: true,
                        formatter: function () {
                            const { age } = this.point
                            const pad = num => String(num).padStart(2, '0');
                            const xDate = this.x instanceof Date ? this.x : new Date(this.x);

                            const formatDate = (date) => {
                                if (last24h) {
                                    return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
                                } else if (dateOnly) {
                                    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
                                } else {
                                    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ` +
                                        `${pad(date.getHours())}:${pad(date.getMinutes())}`;
                                }
                            };
                            let tooltipContent = "";

                            if (this.points) {
                                this.points.forEach(point => {
                                    if (point.series.name === 'Range' || point.series.type === 'arearange') {
                                        tooltipContent += `
                                            <div>
                                                <br />
                                                <span>📊 Max: ${point.point.high} ${unit}</span><br/>
                                                <span>📊 Min: ${point.point.low} ${unit}</span>
                                            </div>
                                        `;
                                    } else {
                                        tooltipContent += `
                                            <div>
                                                <b style="color:${point.color}">${point.series.name}</b><br/>
                                                <span>${this.series.chart.userOptions.tooltipLabels.age}: ${age}</span><br/>
                                                <span>🕒 ${formatDate(xDate)}</span><br/>
                                                <span>📊 ${point.y.toFixed(2)} ${unit}</span>
                                            </div>
                                        `;
                                    }
                                });
                            }

                            return tooltipContent;
                        }
                    },
                    plotOptions: {
                        series: {
                            animation: false,
                            marker: {
                                radius: 1,
                                enabled: true
                            },
                            lineWidth: 2,
                        },
                        area: {
                            animation: false,
                            marker: {
                                enabled: false
                            },
                        },
                    },
                    time: {
                        timezoneOffset: new Date().getTimezoneOffset()
                    },
                    yAxis: {
                        min: unit == 'ºC' ? 10 : 0,
                        title: {
                            enabled: false,
                        },
                        plotBands: [plotBands],
                        labels: {
                            format: `{value} ${unit}`
                        },
                    },
                    credits: {
                        enabled: false
                    },
                    xAxis: {
                        type: 'datetime',
                        tickInterval: last24h ? 3600 * 2000 : null,
                        labels: {
                            format: last24h ? '{value:%H:%M}' : '{value:%e/%m}',
                            rotation: 0
                        },
                        title: {
                            enabled: false,
                        }
                    },
                    series: series,
                }
            }
        },
        convertGraphDataLineLast24h(data = [], serieCO2 = [], device_type = 'd0', key = 'co2', unit = 'ppm', plotBandsLimits = true) {
            const serie = JSON.parse(JSON.stringify(serieCO2));
            const findedData = data[0]?.device_shed_division?.find(item => item.device.device_type === device_type);
            const ruleData = findedData?.rule_valueranged?.find(item => item.data_type.name === key);
            const ppmMax = ruleData?.max || 2000;
            const ppmMin = ruleData?.min || 0;

            const plotBands = plotBandsLimits ? {
                from: ppmMin,
                to: ppmMax,
                color: 'rgba(68, 170, 213, 0.2)'
            } : {};

            const timezoneOffset = new Date().getTimezoneOffset();
            const offsetMs = timezoneOffset * 60000;

            const now = Date.now() - offsetMs;
            const minTime = now - 24 * 60 * 60 * 1000;

            for (let k in serie) {
                let filteredSerie = {};
                serie[k].data.forEach(item => {
                    item[0] = item[0] - offsetMs;
                    const secondsKey = parseInt(item[0] / 1000);
                    filteredSerie[secondsKey] = item;
                });

                serie[k].data = Object.values(filteredSerie);
                serie[k].data.push([now, null]);
            }

            return {
                id: data.id,
                props: {
                    chart: {
                        zooming: { type: 'xy' }
                    },
                    height: 250,
                    periodFrom: null,
                    periodTo: null,
                    legend: { enabled: true },
                    title: {
                        text: 'Últimas 24 horas'
                    },
                    credits: { enabled: false },
                    tooltip: {
                        crosshairs: true,
                        shared: true,
                        formatter: function () {
                            const date = new Date(this.x + offsetMs);
                            let tooltipContent = `<b>${('00' + date.getHours()).slice(-2)}:${('00' + date.getMinutes()).slice(-2)}:${('00' + date.getSeconds()).slice(-2)}</b><br/>`;
                            if (this.points) {
                                this.points.forEach(point => {
                                    tooltipContent += `${point.series.name}: <b>${point.y?.toFixed(2) ?? '—'} ${unit}</b><br/>`;
                                });
                            } else if (this.y != null) {
                                tooltipContent += `${this.series.name}: <b>${this.y.toFixed(2)} ${unit}</b><br/>`;
                            }
                            return tooltipContent;
                        }
                    },
                    plotOptions: {
                        series: {
                            animation: false,
                            marker: { radius: 1, enabled: true },
                            lineWidth: 2,
                        },
                        area: {
                            animation: false,
                            marker: { enabled: false },
                        },
                    },
                    yAxis: {
                        min: unit == 'ºC' ? 10 : 0,
                        title: { enabled: false },
                        plotBands: [plotBands],
                        labels: {
                            format: `{value} ${unit}`
                        },
                    },
                    xAxis: {
                        type: 'datetime',
                        min: minTime,
                        max: now,
                        labels: {
                            format: '{value:%H:%M}'
                        },
                        title: { enabled: false }
                    },
                    series: serie,
                }
            }
        },
    },
    watch: {
        'getSelectedBatch.id': {
            immediate: true,
            async handler(batchId) {
                if (!batchId) return;
                this.$emit('set-loading', true)

                const module = this.$route.meta.module;

                if (module === 'matrix' || module === 'laying-birds') {
                    await this.loadBatchDetails({ id: batchId, state: 'sanity' });
                }
                this.$emit('set-loading', false);
            }
        }
    }
};
</script>
